// SidebarTopItems.js

import React, { useState } from "react";
import { List, ListItemButton, ListItemIcon, ListItemText, Collapse, Box, IconButton } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";

// Import modern sidebar typography styles
import './SidebarTypography.css';

const SidebarTopItems = ({
  open,
  items,
  location,
  navigate,
  isMobile,
  setMobileOpen,
  setProfile,
  onOrganizationCreateCollectionClick,
  organizationId,
}) => {
  const [expandedItems, setExpandedItems] = useState({});

  if (!items || items.length === 0) return null;

  const handleToggle = (itemTitle, e) => {
    e.stopPropagation();
    setExpandedItems((prev) => ({
      ...prev,
      [itemTitle]: !prev[itemTitle],
    }));
  };

  return (
    <List>
      {items.map((item) => {
        const hasNestedChildren = item.children && item.children.length > 0;
        const isActive = location.pathname.includes(item.path);
        const isExpanded = expandedItems[item.title];

        return (
          <React.Fragment key={item.title}>
            <ListItemButton
              className="sidebar-typography"
              sx={{
                minHeight: 36,
                justifyContent: open ? "initial" : "center",
                px: open ? 3 : 2,
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                fontWeight: 500,
                letterSpacing: "-0.01em",
                "&:hover": {
                  backgroundColor: "rgba(99,102,241,0.08)",
                  borderRadius: "8px",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              onClick={(e) => {
                if (hasNestedChildren) {
                  handleToggle(item.title, e);
                } else if (item.path) {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                  setProfile(false);
                }
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: open ? "20px" : "0px",
                  justifyContent: "center",
                  marginRight: open ? "10px" : "0px",
                  transition: 'min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin-right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {open && (
                <>
                  <ListItemText
                    disableTypography
                    primary={item.title}
                    className={`sidebar-typography sidebar-nav-item ${isActive ? 'sidebar-nav-item-active' : ''}`}
                    sx={{
                      marginRight: "10px",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      maxWidth: "200px",
                      color: isActive ? "rgba(99,102,241,0.9)" : "rgba(30,41,59,0.8)",
                      opacity: open ? 1 : 0,
                      transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  />
                  {hasNestedChildren && (
                    <Box sx={{ 
                      ml: "auto",
                      display: "flex",
                      alignItems: "center",
                      opacity: open ? 1 : 0,
                      transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}>
                      {isExpanded ? (
                        <ExpandLess sx={{ 
                          color: "rgba(99,102,241,0.7)", 
                          fontSize: 20 
                        }} />
                      ) : (
                        <ExpandMore sx={{ 
                          color: "rgba(71,85,105,0.6)", 
                          fontSize: 20 
                        }} />
                      )}
                    </Box>
                  )}
                </>
              )}
            </ListItemButton>

            {/* Collapsible nested items */}
            {hasNestedChildren && (
              <Collapse in={isExpanded && open} timeout="auto" unmountOnExit>
                <List sx={{ pl: 4 }}>
                  {item.children.map((child) => {
                    const isOrgCollections = child.path === "/organization/collections" && organizationId && onOrganizationCreateCollectionClick;
                    return (
                      <ListItemButton
                        key={child.title}
                        className="sidebar-typography"
                        sx={{
                          minHeight: 36,
                          justifyContent: "initial",
                          px: 3,
                          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                          fontWeight: 400,
                          letterSpacing: "-0.01em",
                          "&:hover": {
                            backgroundColor: "rgba(99,102,241,0.08)",
                            borderRadius: "8px",
                          },
                          transition: "all 0.2s ease",
                        }}
                        onClick={() => {
                          navigate(child.path);
                          if (isMobile) setMobileOpen(false);
                          setProfile(false);
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: "20px",
                            justifyContent: "center",
                            marginRight: "10px",
                          }}
                        >
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText
                          disableTypography
                          primary={child.title}
                          className="sidebar-typography sidebar-nav-item"
                          sx={{
                            marginRight: "10px",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            maxWidth: "200px",
                            color: "rgba(30,41,59,0.7)",
                            fontSize: "14px",
                            fontWeight: 500,
                          }}
                        />
                        {open && isOrgCollections && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              onOrganizationCreateCollectionClick();
                              if (isMobile) setMobileOpen(false);
                            }}
                            size="small"
                            sx={{
                              background: "rgba(99,102,241,0.1)",
                              borderRadius: "8px",
                              width: 28,
                              height: 28,
                              minWidth: 28,
                              ml: 0.5,
                              "&:hover": {
                                background: "rgba(99,102,241,0.2)",
                              },
                            }}
                          >
                            <AddIcon sx={{ fontSize: 16, color: "rgba(99,102,241,0.8)" }} />
                          </IconButton>
                        )}
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </List>
  );
};

export default SidebarTopItems;
